import { useState, useEffect, useCallback, useRef } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import {
    Activity, CheckCircle2, XCircle, RefreshCw, Zap, Send, Clock, MapPin,
    AlertTriangle, Loader2, History, ChevronDown, ChevronRight, Power,
} from 'lucide-react';
import { welcomeSchedulerAPI } from '../../lib/api';
import { toast } from 'sonner';

/**
 * P_WELCOME_HUB_SCHEDULER_TAB_2.0 — Simplified Scheduler tab.
 *
 * The operator must instantly answer "is the Daily Scheduler ON or
 * OFF?" — so the toggle is the visual centrepiece. Manual triggers
 * (Run Now / Force) sit directly under it as secondary controls.
 * Status cards and recent activity remain but are secondary. Every
 * technical metric the operator does not need on a daily basis
 * (concurrency, queue size, per-IANA distribution) is folded into
 * a collapsed "Advanced diagnostics" section.
 *
 * Theme: shared Card/Badge/Switch primitives so dark mode tokens
 * inherit from the surrounding UserWelcomeDispatch hub.
 */

const fmtDate = (iso, tz) => {
    if (!iso) return '—';
    try {
        const opts = {
            year: 'numeric', month: 'short', day: '2-digit',
            hour: '2-digit', minute: '2-digit',
        };
        if (tz) opts.timeZone = tz;
        return new Date(iso).toLocaleString(undefined, opts);
    } catch { return iso; }
};

const fmtRelative = (iso) => {
    if (!iso) return null;
    try {
        const ms = Date.now() - new Date(iso).getTime();
        if (ms < 0) {
            const abs = Math.abs(ms);
            if (abs < 60_000) return `in ${Math.round(abs / 1000)}s`;
            if (abs < 3_600_000) return `in ${Math.round(abs / 60_000)}m`;
            if (abs < 86_400_000) return `in ${Math.round(abs / 3_600_000)}h`;
            return `in ${Math.round(abs / 86_400_000)}d`;
        }
        if (ms < 60_000) return `${Math.round(ms / 1000)}s ago`;
        if (ms < 3_600_000) return `${Math.round(ms / 60_000)}m ago`;
        if (ms < 86_400_000) return `${Math.round(ms / 3_600_000)}h ago`;
        return `${Math.round(ms / 86_400_000)}d ago`;
    } catch { return null; }
};

const SOURCE_BADGE = {
    scheduled: { tone: 'bg-sky-500/15 text-sky-700 dark:text-sky-300', label: 'Cron' },
    run_now:   { tone: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300', label: 'Run Now' },
    force:     { tone: 'bg-amber-500/15 text-amber-700 dark:text-amber-300', label: 'Forced' },
};

const SchedulerTab = ({ isSuperAdmin = true }) => {
    const [health, setHealth] = useState(null);
    const [runs, setRuns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(null);
    const [error, setError] = useState(null);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const pollRef = useRef(null);

    const reload = useCallback(async () => {
        setError(null);
        try {
            const [h, r] = await Promise.all([
                welcomeSchedulerAPI.health(),
                welcomeSchedulerAPI.recentRuns(10),
            ]);
            setHealth(h.data);
            setRuns(r.data?.runs || []);
        } catch (e) {
            setError(e.response?.data?.detail || e.message || 'Failed to load scheduler state');
        } finally { setLoading(false); }
    }, []);

    useEffect(() => {
        if (!isSuperAdmin) { setLoading(false); return; }
        reload();
        pollRef.current = setInterval(reload, 30_000);
        return () => { if (pollRef.current) clearInterval(pollRef.current); };
    }, [isSuperAdmin, reload]);

    const runAction = async (kind, fn, confirmMsg, toastMsg) => {
        if (busy) return;
        if (confirmMsg && !window.confirm(confirmMsg)) return;
        setBusy(kind);
        setError(null);
        try {
            await fn();
            if (toastMsg) toast.success(toastMsg);
            await reload();
        } catch (e) {
            const detail = e.response?.data?.detail || e.message || 'Action failed';
            setError(detail);
            toast.error(detail);
        } finally { setBusy(null); }
    };

    const onToggle = (next) => {
        if (next === !!health?.running) return; // no-op
        if (!next) {
            // OFF requires confirmation per spec
            return runAction(
                'toggle',
                welcomeSchedulerAPI.disable,
                'Disable automatic daily welcome generation? Users will only receive welcomes when manually triggered.',
                'Daily Scheduler turned OFF',
            );
        }
        return runAction('toggle', welcomeSchedulerAPI.enable, null, 'Daily Scheduler turned ON');
    };

    const onRunNow = () => runAction(
        'run',
        welcomeSchedulerAPI.runNow,
        'Run a tick now? Only organizations currently in their local-time window will be dispatched. The regular schedule is not affected.',
        'Tick executed',
    );
    const onForceDispatch = () => runAction(
        'force',
        welcomeSchedulerAPI.forceDispatch,
        'Force a Welcome Dispatch to EVERY active organization, bypassing local-time windows? Duplicate sends are prevented by the per-user idempotency index.',
        'Force-dispatch executed',
    );

    if (!isSuperAdmin) {
        return (
            <Card className="p-8 text-center" data-testid="scheduler-tab-forbidden">
                <AlertTriangle className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                <div className="text-base font-medium text-foreground">Super admin only</div>
                <div className="text-sm text-muted-foreground mt-1">
                    The Scheduler controls are restricted to super administrators.
                </div>
            </Card>
        );
    }

    if (loading && !health) {
        return (
            <div className="p-8 flex items-center justify-center text-muted-foreground" data-testid="scheduler-tab-loading">
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Loading scheduler state…
            </div>
        );
    }

    const running = !!health?.running;
    const lastErr = health?.last_error;
    const toggleBusy = busy === 'toggle';

    return (
        <div className="space-y-4" data-testid="scheduler-tab-body">
            {error ? (
                <Card className="p-3 border-rose-500/40 bg-rose-500/10 text-rose-700 dark:text-rose-300 flex items-start gap-2" data-testid="scheduler-tab-error">
                    <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <div className="text-sm">{String(error)}</div>
                </Card>
            ) : null}

            {/* ── PRIMARY: Daily Scheduler ON/OFF toggle ─────────────────── */}
            <Card
                className={`p-6 border-2 transition-colors ${
                    running
                        ? 'border-emerald-500/40 bg-emerald-500/5'
                        : 'border-rose-500/40 bg-rose-500/5'
                }`}
                data-testid="scheduler-primary-toggle-card"
            >
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-5">
                    <div className="flex items-center gap-4 min-w-0">
                        <div
                            className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                                running
                                    ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-300'
                                    : 'bg-rose-500/15 text-rose-600 dark:text-rose-300'
                            }`}
                        >
                            <Power className="w-6 h-6" />
                        </div>
                        <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                                <h3 className="text-lg sm:text-xl font-semibold text-foreground">
                                    Daily Scheduler
                                </h3>
                                {running ? (
                                    <Badge
                                        className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/40 gap-1 text-sm"
                                        data-testid="scheduler-status-badge"
                                        data-status="running"
                                    >
                                        <CheckCircle2 className="w-3.5 h-3.5" />
                                        ON
                                    </Badge>
                                ) : (
                                    <Badge
                                        className="bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/40 gap-1 text-sm"
                                        data-testid="scheduler-status-badge"
                                        data-status="paused"
                                    >
                                        <XCircle className="w-3.5 h-3.5" />
                                        OFF
                                    </Badge>
                                )}
                                {health?.runtime_override !== null && health?.runtime_override !== undefined ? (
                                    <Badge variant="outline" className="text-[10px]" data-testid="scheduler-override-badge">
                                        Runtime override
                                    </Badge>
                                ) : null}
                            </div>
                            <p className="text-sm text-muted-foreground mt-1 max-w-md">
                                {running
                                    ? 'Daily welcome dispatches are running automatically on each organization\'s local schedule.'
                                    : 'Automatic daily dispatches are paused. Users will only receive welcomes when manually triggered.'}
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-3 sm:gap-4">
                        {toggleBusy ? (
                            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                        ) : null}
                        <Switch
                            checked={running}
                            disabled={!!busy}
                            onCheckedChange={onToggle}
                            data-testid="scheduler-primary-toggle"
                            data-state-value={running ? 'on' : 'off'}
                            className="scale-125"
                        />
                    </div>
                </div>
            </Card>

            {/* ── SECONDARY: Manual triggers ────────────────────────────── */}
            <Card className="p-4" data-testid="scheduler-manual-controls">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="text-sm text-muted-foreground">
                        Manual triggers — these run regardless of the toggle and never affect the regular schedule.
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={onRunNow}
                            disabled={!!busy}
                            data-testid="scheduler-run-now-btn"
                        >
                            {busy === 'run' ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Zap className="w-3.5 h-3.5 mr-1.5" />}
                            Run Now
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={onForceDispatch}
                            disabled={!!busy}
                            data-testid="scheduler-force-btn"
                            className="text-amber-700 dark:text-amber-300 border-amber-500/40 hover:bg-amber-500/10"
                        >
                            {busy === 'force' ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1.5" />}
                            Force Welcome Dispatch
                        </Button>
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 px-2"
                            onClick={reload}
                            disabled={!!busy}
                            data-testid="scheduler-reload-btn"
                            title="Refresh"
                        >
                            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                        </Button>
                    </div>
                </div>
            </Card>

            {/* ── SECONDARY: At-a-glance status (next / last / due) ─────── */}
            <Card className="p-5" data-testid="scheduler-at-a-glance">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
                    <div data-testid="scheduler-tab-next-run">
                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground uppercase tracking-wide">
                            <Clock className="w-3.5 h-3.5" /><span>Next run</span>
                        </div>
                        <div className="text-base font-semibold text-foreground mt-1">
                            {fmtDate(health?.next_run_local, health?.platform_timezone)}
                        </div>
                        <div className="text-xs text-muted-foreground">
                            {fmtRelative(health?.next_run_local) || (running ? 'pending' : 'paused')}
                        </div>
                    </div>
                    <div data-testid="scheduler-tab-last-tick">
                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground uppercase tracking-wide">
                            <Activity className="w-3.5 h-3.5" /><span>Last run</span>
                        </div>
                        <div className="text-base font-semibold text-foreground mt-1">
                            {fmtDate(health?.last_tick_at, health?.platform_timezone)}
                        </div>
                        <div className="text-xs text-muted-foreground">
                            {fmtRelative(health?.last_tick_at) || 'no ticks yet'}
                        </div>
                    </div>
                    <div data-testid="health-orgs-due">
                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground uppercase tracking-wide">
                            <MapPin className="w-3.5 h-3.5" /><span>Orgs due now</span>
                        </div>
                        <div className="text-base font-semibold text-foreground mt-1">{health?.organizations_due_now ?? 0}</div>
                        <div className="text-xs text-muted-foreground">{health?.organizations_scanned ?? 0} active</div>
                    </div>
                    <div data-testid="health-success-24h">
                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground uppercase tracking-wide">
                            <CheckCircle2 className="w-3.5 h-3.5" /><span>Successful (24h)</span>
                        </div>
                        <div className={`text-base font-semibold mt-1 ${lastErr ? 'text-rose-600 dark:text-rose-300' : 'text-foreground'}`}>
                            {health?.successful_runs_24h ?? 0}{lastErr ? ' · errors detected' : ''}
                        </div>
                        <div className="text-xs text-muted-foreground">{lastErr ? 'see advanced diagnostics' : 'no recent errors'}</div>
                    </div>
                </div>
            </Card>

            {/* ── SECONDARY: Recent activity ────────────────────────────── */}
            <Card className="p-5 space-y-3" data-testid="scheduler-tab-recent-runs">
                <div className="flex items-center gap-2">
                    <History className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm font-semibold text-foreground">Recent activity</span>
                    <span className="text-xs text-muted-foreground">last {runs.length} of up to 50</span>
                </div>
                {runs.length === 0 ? (
                    <div
                        className="text-sm text-muted-foreground border border-dashed border-border rounded-md py-6 text-center"
                        data-testid="recent-runs-empty"
                    >
                        No scheduler runs have been recorded yet. Trigger one with Run Now or Force Welcome Dispatch above.
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="recent-runs-table">
                            <thead>
                                <tr className="text-left text-[11px] uppercase tracking-wide text-muted-foreground border-b border-border">
                                    <th className="py-2 pr-3 font-medium">Started</th>
                                    <th className="py-2 pr-3 font-medium">Source</th>
                                    <th className="py-2 pr-3 font-medium">Duration</th>
                                    <th className="py-2 pr-3 font-medium">Orgs</th>
                                    <th className="py-2 pr-3 font-medium">Sent</th>
                                    <th className="py-2 pr-3 font-medium">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {runs.map((r, idx) => {
                                    const src = SOURCE_BADGE[r.source] || { tone: 'bg-muted text-foreground', label: r.source || '—' };
                                    return (
                                        <tr key={r.started_at + '-' + idx} className="border-b border-border last:border-b-0" data-testid={`recent-run-row-${idx}`}>
                                            <td className="py-2 pr-3 text-foreground whitespace-nowrap">
                                                {fmtDate(r.started_at, health?.platform_timezone)}
                                                <div className="text-[11px] text-muted-foreground">{fmtRelative(r.started_at)}</div>
                                            </td>
                                            <td className="py-2 pr-3">
                                                <Badge className={`text-[11px] border ${src.tone} border-transparent`}>{src.label}</Badge>
                                            </td>
                                            <td className="py-2 pr-3 text-muted-foreground">{r.duration_ms != null ? `${r.duration_ms} ms` : '—'}</td>
                                            <td className="py-2 pr-3 text-muted-foreground">{r.organizations_due_now ?? 0} / {r.organizations_scanned ?? 0}</td>
                                            <td className="py-2 pr-3 text-foreground">{r.created ?? 0}</td>
                                            <td className="py-2 pr-3">
                                                {r.success ? (
                                                    <Badge className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/40 text-[11px]">OK</Badge>
                                                ) : (
                                                    <Badge className="bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/40 text-[11px]">Failed</Badge>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>

            {/* ── ADVANCED DIAGNOSTICS (collapsible) ─────────────────────── */}
            <Card className="p-0 overflow-hidden" data-testid="scheduler-advanced-card">
                <button
                    type="button"
                    onClick={() => setShowAdvanced(s => !s)}
                    className="w-full px-5 py-3 flex items-center justify-between text-left hover:bg-muted/50 transition-colors"
                    data-testid="scheduler-advanced-toggle"
                    aria-expanded={showAdvanced}
                >
                    <div className="flex items-center gap-2">
                        {showAdvanced ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                        <span className="text-sm font-semibold text-foreground">Advanced diagnostics</span>
                        <span className="text-xs text-muted-foreground hidden sm:inline">
                            timezone breakdown · concurrency · queue · last error
                        </span>
                    </div>
                </button>
                {showAdvanced ? (
                    <div className="px-5 pb-5 pt-1 space-y-4 border-t border-border" data-testid="scheduler-advanced-body">
                        {lastErr ? (
                            <div className="text-xs text-rose-700 dark:text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-md px-3 py-2" data-testid="health-last-error-detail">
                                <div className="flex items-center gap-2">
                                    <AlertTriangle className="w-3.5 h-3.5" />
                                    <span className="font-medium">Most recent error</span>
                                    <span className="text-muted-foreground">· {fmtDate(lastErr.started_at, health?.platform_timezone)}</span>
                                </div>
                                <div className="font-mono text-[11px] mt-1 break-all">{lastErr.last_error || `${lastErr.failed} user(s) failed`}</div>
                            </div>
                        ) : null}

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div data-testid="adv-platform-tz">
                                <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Platform timezone</div>
                                <div className="font-medium text-foreground mt-0.5">{health?.platform_timezone || '—'}</div>
                                <div className="text-[11px] text-muted-foreground">Tick every {health?.tick_cadence_minutes ?? 5} min</div>
                            </div>
                            <div data-testid="adv-users-due">
                                <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Users due now</div>
                                <div className="font-medium text-foreground mt-0.5">{health?.users_due_now ?? 0}</div>
                                <div className="text-[11px] text-muted-foreground">across due orgs</div>
                            </div>
                            <div data-testid="adv-concurrency">
                                <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Concurrency</div>
                                <div className="font-medium text-foreground mt-0.5">{health?.fanout_concurrency ?? '—'}</div>
                                <div className="text-[11px] text-muted-foreground">parallel users per tick</div>
                            </div>
                            <div data-testid="adv-queue">
                                <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Queue size</div>
                                <div className="font-medium text-foreground mt-0.5">{health?.queue_size ?? 0}</div>
                                <div className="text-[11px] text-muted-foreground">pending</div>
                            </div>
                        </div>

                        {health?.timezone_count && Object.keys(health.timezone_count).length > 0 ? (
                            <div data-testid="adv-tz-badges">
                                <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1.5">Timezones in use</div>
                                <div className="flex flex-wrap items-center gap-1.5">
                                    {Object.entries(health.timezone_count).map(([tz, count]) => (
                                        <Badge key={tz} variant="outline" className="text-[11px]">
                                            {tz} <span className="ml-1 text-muted-foreground">×{count}</span>
                                        </Badge>
                                    ))}
                                </div>
                            </div>
                        ) : null}
                    </div>
                ) : null}
            </Card>
        </div>
    );
};

export default SchedulerTab;
